import {
  ADVENTURE_DUNGEON_MAP_ID,
  BEACH_BATTLES_KOTH_MAP_ID,
  ARENA_PVP_MAP_ID,
  BATTLE_ROYALE_ISLAND_MAP_ID,
  BUILD_FIGHT_ARENA_MAP_ID,
  FREEPLAY_OPEN_WORLD_MAP_ID,
  HUB_MAP_ID,
  SMOOTH_COASTAL_DRIVE_MAP_ID,
  SMOOTH_METRO_FREEPLAY_MAP_ID,
  SMOOTH_ROYALE_HIGHLANDS_MAP_ID,
  SURVIVE_KOMODO_ISLAND_MAP_ID,
  TEAM_ARENA_MAP_ID,
} from './mapManifests.js';

export type OfficialPlayStyle =
  | 'social'
  | 'freeplay'
  | 'driving'
  | 'adventure'
  | 'arena'
  | 'teams'
  | 'royale'
  | 'build';

export type OfficialWorldKind =
  | 'hub'
  | 'open-world'
  | 'city'
  | 'arena'
  | 'island'
  | 'dungeon'
  | 'build-box';

export interface OfficialMapInfo {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly kind: OfficialWorldKind;
  readonly scale: string;
  readonly recommendedFor: readonly OfficialPlayStyle[];
  readonly tags: readonly string[];
}

export const OFFICIAL_MAPS: readonly OfficialMapInfo[] = Object.freeze([
  {
    id: HUB_MAP_ID,
    name: 'Portal Hub',
    description: 'Compact social spawn with portals into public modes.',
    kind: 'hub',
    scale: '24 players',
    recommendedFor: ['social'],
    tags: ['hub', 'queue', 'safe'],
  },
  {
    id: SMOOTH_METRO_FREEPLAY_MAP_ID,
    name: 'Freeplay Island City',
    description: 'Continuous island freeplay world with named city, airport, harbor, industrial, park, desert, and racing districts.',
    kind: 'city',
    scale: 'hundreds',
    recommendedFor: ['freeplay', 'driving', 'adventure'],
    tags: ['smooth', 'island', 'city', 'airport', 'vehicles'],
  },
  {
    id: SMOOTH_COASTAL_DRIVE_MAP_ID,
    name: 'Smooth Coastal Drive',
    description: 'Wide-road coastal test world with airport runway, shoreline highway, mountain road, and sparse buildings.',
    kind: 'open-world',
    scale: 'large',
    recommendedFor: ['driving', 'freeplay'],
    tags: ['smooth', 'driving', 'coast', 'airport', 'mountains'],
  },
  {
    id: SMOOTH_ROYALE_HIGHLANDS_MAP_ID,
    name: 'Smooth Royale Highlands',
    description: 'Continuous highland island with loop roads, compounds, open fields, ridges, and royale-scale sightlines.',
    kind: 'island',
    scale: 'large',
    recommendedFor: ['royale', 'freeplay', 'adventure'],
    tags: ['smooth', 'royale', 'hills', 'compounds', 'vehicles'],
  },
  {
    id: FREEPLAY_OPEN_WORLD_MAP_ID,
    name: 'Procedural Metro Prototype',
    description: 'Large generated terrain kept as a fallback while authored worlds are validated.',
    kind: 'open-world',
    scale: 'hundreds',
    recommendedFor: ['freeplay', 'driving', 'adventure'],
    tags: ['generated', 'fallback', 'vehicles'],
  },
  {
    id: SURVIVE_KOMODO_ISLAND_MAP_ID,
    name: 'Komodo Block Import',
    description: 'Minecraft-derived authored island kept for local visual comparison; not recommended for fast vehicle modes.',
    kind: 'island',
    scale: 'large authored',
    recommendedFor: ['adventure'],
    tags: ['blocks', 'comparison', 'minecraft-import'],
  },
  {
    id: BEACH_BATTLES_KOTH_MAP_ID,
    name: 'Beach Battles',
    description: 'Small beach combat map for focused arena play.',
    kind: 'arena',
    scale: 'small',
    recommendedFor: ['arena', 'teams'],
    tags: ['koth', 'beach', 'combat'],
  },
  {
    id: ADVENTURE_DUNGEON_MAP_ID,
    name: 'Dungeon Run',
    description: 'Authored room-and-corridor adventure layout for objective play.',
    kind: 'dungeon',
    scale: 'party',
    recommendedFor: ['adventure'],
    tags: ['pve', 'rooms', 'objectives'],
  },
  {
    id: ARENA_PVP_MAP_ID,
    name: 'Arena Blocks',
    description: 'Tight free-for-all arena with cover and fast respawns.',
    kind: 'arena',
    scale: 'small',
    recommendedFor: ['arena'],
    tags: ['ffa', 'cover', 'combat'],
  },
  {
    id: TEAM_ARENA_MAP_ID,
    name: 'Team Control Arena',
    description: 'Symmetric team map with protected spawns and a center fight lane.',
    kind: 'arena',
    scale: 'medium',
    recommendedFor: ['teams'],
    tags: ['teams', 'mid', 'scrim'],
  },
  {
    id: BATTLE_ROYALE_ISLAND_MAP_ID,
    name: 'Royale Island',
    description: 'Island combat map with scattered cover and a visible zone ring.',
    kind: 'island',
    scale: 'large',
    recommendedFor: ['royale'],
    tags: ['last-standing', 'loot', 'zone'],
  },
  {
    id: BUILD_FIGHT_ARENA_MAP_ID,
    name: 'Build Fight Box',
    description: 'Flat combat box tuned for quick cover-building fights.',
    kind: 'build-box',
    scale: 'duos',
    recommendedFor: ['build', 'arena'],
    tags: ['building', 'duel', 'cover'],
  },
]);

export function officialMapById(mapId: string): OfficialMapInfo | undefined {
  return OFFICIAL_MAPS.find((map) => map.id === mapId);
}
